import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AccionesDialog } from '@models/base/identificadores.model';
import { TipoMovimientoDto } from '@models/tipos-movimiento/tipoMovimientoDto.model';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { DialogCreateTipoMovimientoComponent } from '@views/tipos-movimientos/crear-tipo-movimiento/dialog-create-tipo-movimiento/dialog-create-tipo-movimiento.component';
import { DialogUpdateTipoMovimientoComponent } from '@views/tipos-movimientos/actualizar-tipo-movimiento/dialog-update-tipo-movimiento/dialog-update-tipo-movimiento.component';

/**
 * Buscador de Tipo de Movimiento con autocompletado.
 *
 * Reemplaza al mat-select clásico. Suma un menú de tres puntos para dar de alta
 * un tipo nuevo o editar el seleccionado, sin salir del formulario.
 *
 * IMPORTANTE: la lista de opciones se recibe por @Input y NO se pide acá adentro.
 * Cada pantalla aplica sus propias reglas (ids permitidos / excluidos según el
 * contexto), y ese filtrado no se puede perder.
 */
@Component({
  selector: 'app-buscar-tipo-movimiento',
  templateUrl: './buscar-tipo-movimiento.component.html',
  styleUrls: ['./buscar-tipo-movimiento.component.css']
})
export class BuscarTipoMovimientoComponent implements OnInit, OnChanges {

  /** Opciones a mostrar, ya filtradas por la pantalla que lo usa */
  @Input() tiposMovimiento: TipoMovimientoDto[] = [];

  /** Id a preseleccionar. Sirve para los formularios de edición. */
  @Input() idTipoMovimiento: number | null = null;

  @Input() esRequerido: boolean = false;
  @Input() deshabilitado: boolean = false;
  @Input() etiqueta: string = "Tipo Movimiento";

  /** Muestra el menú de tres puntos con Nuevo / Editar */
  @Input() permiteAlta: boolean = true;

  @Output() eventTipoMovimiento = new EventEmitter<TipoMovimientoDto | null>();

  public control = new FormControl();
  public opcionesFiltradas!: Observable<TipoMovimientoDto[]>;

  seleccionado: TipoMovimientoDto | null = null;

  /**
   * La lista llega por HTTP después de ngOnInit. Se publica en un Subject para
   * que el autocomplete se vuelva a evaluar cuando llega, en vez de quedarse
   * con el array vacío de la primera emisión.
   */
  private opciones$ = new BehaviorSubject<TipoMovimientoDto[]>([]);

  constructor(
    private dialog: MatDialog,
    private snackBar: SnackBarService,
  ) { }

  ngOnInit(): void {
    this.aplicarValidadores();

    // Se recalcula cuando cambia el texto tipeado O cuando llega/cambia la lista.
    this.opcionesFiltradas = combineLatest([
      this.control.valueChanges.pipe(startWith(this.control.value)),
      this.opciones$
    ]).pipe(
      map(([valor, opciones]) => this.filtrar(valor, opciones))
    );

    this.opciones$.next(this.tiposMovimiento ?? []);
    this.preseleccionar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['esRequerido'])
      this.aplicarValidadores();

    if (changes['deshabilitado'])
      this.deshabilitado ? this.control.disable() : this.control.enable();

    // La lista suele llegar después del ngOnInit: hay que republicarla.
    if (changes['tiposMovimiento'])
      this.opciones$.next(this.tiposMovimiento ?? []);

    // Cuando llega la lista o cambia el id de afuera, hay que reflejarlo.
    if (changes['idTipoMovimiento'] || changes['tiposMovimiento'])
      this.preseleccionar();
  }

  private aplicarValidadores() {
    if (this.esRequerido)
      this.control.setValidators([Validators.required]);
    else
      this.control.clearValidators();

    this.control.updateValueAndValidity({ emitEvent: false });
  }

  /** Deja el control mostrando el tipo que viene por @Input */
  private preseleccionar() {
    if (!this.idTipoMovimiento || !this.tiposMovimiento?.length) {
      if (!this.idTipoMovimiento) {
        this.seleccionado = null;
        this.control.setValue('', { emitEvent: false });
      }
      return;
    }

    const tipo = this.tiposMovimiento.find(t => t.id === this.idTipoMovimiento);

    if (tipo) {
      this.seleccionado = tipo;
      this.control.setValue(tipo, { emitEvent: false });
    }
  }

  private filtrar(valor: any, opciones: TipoMovimientoDto[]): TipoMovimientoDto[] {
    if (!opciones?.length)
      return [];

    // Si el valor NO es texto es porque hay una opción elegida (el control guarda
    // el DTO). En ese caso se lista todo, para poder ver las demás y cambiarla.
    // Sólo se filtra cuando el usuario efectivamente está escribiendo.
    if (typeof valor !== 'string')
      return opciones;

    const busqueda = valor.trim().toLowerCase();

    if (!busqueda)
      return opciones;

    return opciones.filter(t => t.descripcion?.toLowerCase().includes(busqueda));
  }

  /** Texto que muestra el input una vez elegida la opción */
  mostrarDescripcion(tipo: TipoMovimientoDto): string {
    return tipo?.descripcion ?? '';
  }

  /**
   * Al hacer foco o click se republica la lista para que el panel muestre todas
   * las opciones. Sin esto, si el usuario había tipeado algo y se fue sin elegir,
   * al volver seguía viendo el listado filtrado por ese texto viejo.
   */
  abrirListado() {
    this.opciones$.next(this.tiposMovimiento ?? []);
  }

  onSeleccion(tipo: TipoMovimientoDto) {
    this.seleccionado = tipo;
    this.eventTipoMovimiento.emit(tipo);
  }

  limpiar() {
    this.seleccionado = null;
    this.control.setValue('');
    this.eventTipoMovimiento.emit(null);
  }

  /** Alta rápida: al confirmar, el tipo nuevo queda seleccionado */
  nuevoTipoMovimiento() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = "30%";

    const dialogRef = this.dialog.open(DialogCreateTipoMovimientoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result || result.event !== AccionesDialog.Agregar)
        return;

      const creado = result.data as TipoMovimientoDto;

      if (!creado?.id) {
        // El alta funcionó pero no volvió el id: no se puede autoseleccionar.
        this.snackBar.showError("Se creó el tipo de movimiento, pero hay que seleccionarlo manualmente.", "Atencion");
        return;
      }

      this.tiposMovimiento = [...this.tiposMovimiento, creado]
        .sort((a, b) => a.descripcion.localeCompare(b.descripcion));

      // Republicar para que el autocomplete vea el tipo nuevo
      this.opciones$.next(this.tiposMovimiento);

      this.seleccionado = creado;
      this.control.setValue(creado);
      this.eventTipoMovimiento.emit(creado);
    });
  }

  /** Edita el tipo seleccionado y refresca la descripción en la lista */
  editarTipoMovimiento() {
    if (!this.seleccionado) {
      this.snackBar.showError("Seleccione un tipo de movimiento para editar.", "Atencion");
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = "30%";
    dialogConfig.data = this.seleccionado;

    const dialogRef = this.dialog.open(DialogUpdateTipoMovimientoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result || result.event !== AccionesDialog.Modificar)
        return;

      const editado = result.data as TipoMovimientoDto;

      if (editado?.descripcion && this.seleccionado) {
        this.seleccionado.descripcion = editado.descripcion;
        this.control.setValue(this.seleccionado);
        this.eventTipoMovimiento.emit(this.seleccionado);
      }
    });
  }

  trackByItems(index: number, item: any): any { return item; }
}
